import React, { useState, useEffect } from "react";
import { useParams, useLocation, Link, useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import SectionArticle from "./SectionArticle/sectionArticle";
import Article from "./Article/Article";
import "./textdelois.css";
import DecisionLieeArticle from "./DecisionLierArticle/DecisionLieeArticle";
import MobileNavigation from "./Mobile/MobileNavigation";

const DetailLois = () => {
    const { id } = useParams();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const legal_text_id = queryParams.get('legal_text_id');

    const [articleData, setArticleData] = useState(null);
    const [summaryData, setSummaryData] = useState(null);
    const [decisionsData, setDecisionsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingDecisions, setLoadingDecisions] = useState(true);
    const [loadingSummary, setLoadingSummary] = useState(true);
    const [searchInput, setSearchInput] = useState("");
    const [screenSize, setScreenSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    });
    const navigate = useNavigate();

    useEffect(() => {
        const handleResize = () => {
            setScreenSize({
                width: window.innerWidth,
                height: window.innerHeight
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Reset decisions quand l'article change
    useEffect(() => {
        setDecisionsData(null);
        setLoadingDecisions(true);
    }, [id]);

    useEffect(() => {
        const fetchArticleData = async () => {
            if (!id) {
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                const url_base = process.env.REACT_APP_API_URL || '';
                const response = await fetch(`${url_base}/lois/article/${id}/detail/`);

                if (!response.ok) {
                    throw new Error(`Error fetching article: ${response.status}`);
                }

                const data = await response.json();
                setArticleData(data);
            } catch (error) {
                console.error("Error fetching article:", error);
                setArticleData(null);
            } finally {
                setLoading(false);
            }
        };

        fetchArticleData();
    }, [id]);

    // Fetch décisions liées via POST une fois articleData chargé
    useEffect(() => {
        const fetchDecisionsData = async () => {
            if (!articleData?.current_article?.number) {
                setLoadingDecisions(false);
                return;
            }

            setLoadingDecisions(true);
            try {
                const url_base = process.env.REACT_APP_API_URL || '';
                const searchQuery = `${articleData.current_article.number}${articleData.legal_text_title ? ` ${articleData.legal_text_title}` : ''}`;
                const formData = new FormData();
                formData.append('search', searchQuery);

                const response = await fetch(`${url_base}/lois/es/search/decisions-by-legal-text/`, {
                    method: 'POST',
                    body: formData,
                });

                if (!response.ok) {
                    throw new Error(`Error fetching decisions: ${response.status}`);
                }

                const data = await response.json();
                setDecisionsData(data.results || []);
            } catch (error) {
                console.error("Error fetching decisions:", error);
                setDecisionsData([]);
            } finally {
                setLoadingDecisions(false);
            }
        };

        fetchDecisionsData();
    }, [articleData]);

    useEffect(() => {
        const fetchSummaryData = async () => {
            if (!legal_text_id) {
                setLoadingSummary(false);
                return;
            }

            setLoadingSummary(true);
            try {
                const url_base = process.env.REACT_APP_API_URL || '';
                const response = await fetch(`${url_base}/lois/legaltext/${legal_text_id}/summary/`);

                if (!response.ok) {
                    throw new Error(`Error fetching summary: ${response.status}`);
                }

                const data = await response.json();
                setSummaryData({
                    ...data,
                    legal_text_id: legal_text_id
                });
            } catch (error) {
                console.error("Error fetching summary:", error);
                setSummaryData(null);
            } finally {
                setLoadingSummary(false);
            }
        };

        fetchSummaryData();
    }, [legal_text_id]);

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchInput.trim()) {
            navigate(`/Loisresult?query=${encodeURIComponent(searchInput)}`);
        }
    };

    const handleLogin = () => {
        window.location.href = "https://datagreffe.ci/dashboard";
    };

    const handleNavigateToArticle = (articleId) => {
        if (!articleId) return;
        navigate(`/article/${articleId}${legal_text_id ? `?legal_text_id=${legal_text_id}` : ''}`);
    };

    const decisionsSearchQuery = articleData?.current_article?.number
        ? `${articleData.current_article.number}${articleData.legal_text_title ? ` ${articleData.legal_text_title}` : ''}`
        : '';

    const mobileMenuContent = (
        <>
            {!loadingSummary && summaryData && <SectionArticle summaryData={summaryData} />}
            {loadingDecisions ? (
                <div className="loading">Chargement des décisions liées...</div>
            ) : (
                <DecisionLieeArticle
                    articleId={id}
                    decision={decisionsData || []}
                    searchQuery={decisionsSearchQuery}
                />
            )}
        </>
    );

    return (
        <div className="detail-lois-wrapper">
            {/* Header */}
            <header className="lois-header">
                <div className="lois-header-left">
                    <Link to="/" className="lois-logo-link">
                        <div className="lois-logo-badge">DG</div>
                        <span className="lois-logo-text">Datagreffe justice</span>
                    </Link>
                </div>

                <div className="lois-search-container">
                    <form onSubmit={handleSearch} className="lois-search-form">
                        <span className="lois-search-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8"></circle>
                                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                            </svg>
                        </span>
                        <input
                            type="text"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder={screenSize.width <= 480 ? "Rechercher..." : "Rechercher un article de loi, un code, une référence..."}
                            className="lois-search-input"
                        />
                        <button type="submit" className="lois-search-btn">
                            {screenSize.width > 480 ? "Rechercher" : "GO"}
                        </button>
                    </form>
                </div>

                <div className="lois-header-right">
                    <button className="lois-login-btn" onClick={handleLogin}>
                        Se connecter
                    </button>
                </div>
            </header>

            {/* Mobile Navigation */}
            <MobileNavigation
                onSearch={handleSearch}
                searchInput={searchInput}
                setSearchInput={setSearchInput}
                handleLogin={handleLogin}
            >
                {mobileMenuContent}
            </MobileNavigation>

            {/* Content */}
            <div className="detail-lois-container">
                <div className="detail-header">
                    <Link to="/Loisresult" className="back-button">
                        <ChevronLeft size={20} />
                        Retour aux résultats
                    </Link>

                    {!loading && articleData && articleData.current_article && (
                        <div className="breadcrumb">
                            <Link to="/">Accueil</Link>
                            <span className="breadcrumb-separator">›</span>
                            <Link to="/Loisresult">Textes</Link>
                            <span className="breadcrumb-separator">›</span>
                            {legal_text_id && articleData.legal_text_title && (
                                <>
                                    <span className="breadcrumb-text">{articleData.legal_text_title}</span>
                                    <span className="breadcrumb-separator">›</span>
                                </>
                            )}
                            <span className="current-page">{articleData.current_article.number}</span>
                        </div>
                    )}
                </div>

                <div className="detail-content">
                    <div className="sidebar">
                        {loadingSummary ? (
                            <div className="loading">Chargement du sommaire...</div>
                        ) : (
                            <SectionArticle summaryData={summaryData} />
                        )}
                    </div>

                    <div className="main-content">
                        {loading ? (
                            <div className="loading">Chargement de l'article...</div>
                        ) : articleData ? (
                            <>
                                <Article
                                    article={articleData}
                                    onNavigateToPrevious={() => articleData.previous_article && handleNavigateToArticle(articleData.previous_article.id)}
                                    onNavigateToNext={() => articleData.next_article && handleNavigateToArticle(articleData.next_article.id)}
                                />
                                <div className="decision-liee-section">
                                    {loadingDecisions ? (
                                        <div className="loading">Chargement des décisions liées...</div>
                                    ) : (
                                        <DecisionLieeArticle
                                            articleId={id}
                                            decision={decisionsData || []}
                                            searchQuery={decisionsSearchQuery}
                                        />
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="error-message">
                                Impossible de charger l'article. Veuillez réessayer.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DetailLois;